// Keyboard shortcuts for preset switching
// Cmd+1..9 switches to the corresponding preset (by sort order)
// Cmd+0 goes back to the preset grid

import { useEffect, useCallback } from 'react';
import type { Preset } from '../services/preset-types';

interface UsePresetShortcutsOptions {
  presets: Preset[];
  activePresetId: string | null;
  onSwitchPreset: (preset: Preset) => void;
  onBackToGrid: () => void;
  enabled?: boolean;
}

export function usePresetShortcuts({
  presets,
  activePresetId,
  onSwitchPreset,
  onBackToGrid,
  enabled = true,
}: UsePresetShortcutsOptions): void {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled) return;

    // Only respond to Cmd+digit (Mac) or Ctrl+digit (Win/Linux)
    const isMod = e.metaKey || e.ctrlKey;
    if (!isMod) return;

    // Ignore if typing in an input/textarea/contenteditable
    const tag = (e.target as HTMLElement)?.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;

    const digit = parseInt(e.key);
    if (isNaN(digit)) return;

    e.preventDefault();
    e.stopPropagation();

    if (digit === 0) {
      onBackToGrid();
      return;
    }

    // 1-indexed: Cmd+1 = first preset, Cmd+9 = ninth
    const index = digit - 1;
    if (index < presets.length) {
      const target = presets[index];
      if (target.id !== activePresetId) {
        onSwitchPreset(target);
      }
    }
  }, [presets, activePresetId, onSwitchPreset, onBackToGrid, enabled]);

  useEffect(() => {
    if (!enabled) return;
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown, enabled]);
}
